import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import toast from "react-hot-toast";
import TemplatePicker from "./TemplatePicker";
import { ACCENT, ACCENT_LIGHT, PINK_BORDER, greenGradientBtn } from "../../churnStyles";

const MAX_ATTACHMENTS = 5;

const EMOJIS = [
  "😀", "😃", "😄", "😁", "😊", "🙂", "😉", "😍",
  "🥰", "😘", "🤗", "🤩", "😎", "🙌", "👏", "👍",
  "🙏", "💪", "✨", "🌟", "🔥", "💯", "🎉", "🥳",
  "❤️", "💖", "💐", "🌸", "☀️", "😅", "🤝", "📅",
  "⏰", "✅", "❗", "❓", "😢", "😔", "🤔", "👋",
];

const ChatComposer = ({
  onSend,
  sending,
  disabled,
  allowAttachments = true,
  sendLabel = "Send SMS",
  placeholder = "Type an SMS…",
  templateContext,
}) => {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]); // File objects
  const [previews, setPreviews] = useState([]); // object URLs, revoked on change
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [templateAnchor, setTemplateAnchor] = useState(null);
  const fileInputRef = useRef(null);
  const textRef = useRef(null);
  // A full-width send label ("Send App Msg") eats ~145px, which leaves the
  // input a couple of characters wide on a phone — below this width the send
  // button becomes an icon so the field keeps the room.
  const compact = useMediaQuery("(max-width:600px)");

  // One object URL per attachment, revoked whenever the list changes or the
  // composer unmounts — re-creating them in render leaks a blob per keystroke.
  useEffect(() => {
    const urls = attachments.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [attachments]);

  const addFiles = (files) => {
    const images = Array.from(files).filter((file) => file.type.startsWith("image/"));
    if (!images.length) return;
    setAttachments((prev) => {
      const next = [...prev, ...images].slice(0, MAX_ATTACHMENTS);
      if (prev.length + images.length > MAX_ATTACHMENTS) {
        toast.error(`Maximum ${MAX_ATTACHMENTS} images per message`);
      }
      return next;
    });
  };

  const handlePaste = (event) => {
    if (!allowAttachments) return;
    const items = Array.from(event.clipboardData?.items || []);
    const files = items
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (files.length) {
      event.preventDefault();
      addFiles(files);
    }
  };

  const insertEmoji = (emoji) => {
    const input = textRef.current;
    const start = input?.selectionStart ?? text.length;
    const end = input?.selectionEnd ?? text.length;
    setText((prev) => prev.slice(0, start) + emoji + prev.slice(end));
    setEmojiAnchor(null);
    input?.focus();
  };

  // Templates land at the caret like an emoji, so a staff member can top-and-
  // tail canned wording instead of losing what they already typed.
  const insertTemplate = (body) => {
    const input = textRef.current;
    const start = input?.selectionStart ?? text.length;
    const end = input?.selectionEnd ?? text.length;
    setText((prev) => prev.slice(0, start) + body + prev.slice(end));
    input?.focus();
  };

  const handleSend = async () => {
    const body = text.trim();
    if ((!body && attachments.length === 0) || sending || disabled) return;
    const ok = await onSend(body, attachments);
    if (ok) {
      setText("");
      setAttachments([]);
    }
  };

  return (
    <Box sx={{ borderTop: `1px solid ${PINK_BORDER}`, p: 1.5, flexShrink: 0 }}>
      {attachments.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
          {attachments.map((file, index) => (
            <Box key={index} sx={{ position: "relative" }}>
              <Box
                component="img"
                src={previews[index]}
                alt={file.name}
                sx={{
                  width: 56,
                  height: 56,
                  objectFit: "cover",
                  borderRadius: "10px",
                  border: `1px solid ${PINK_BORDER}`,
                }}
              />
              <IconButton
                size="small"
                onClick={() =>
                  setAttachments((prev) => prev.filter((_, i) => i !== index))
                }
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  width: 20,
                  height: 20,
                  background: "#fff",
                  border: "1px solid #E5E7EB",
                  "&:hover": { background: "#FEE2E2" },
                }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "flex-end", gap: compact ? 0.75 : 1 }}>
        <IconButton
          onClick={(e) => setEmojiAnchor(e.currentTarget)}
          disabled={disabled}
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: "10px",
            background: ACCENT_LIGHT,
            color: ACCENT,
            "&:hover": { background: "#F8D2DD" },
          }}
        >
          <EmojiEmotionsOutlinedIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <IconButton
          onClick={(e) => setTemplateAnchor(e.currentTarget)}
          disabled={disabled}
          title="Insert a message template"
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: ACCENT_LIGHT,
            color: ACCENT,
            "&:hover": { background: "#F8D2DD" },
          }}
        >
          <TextSnippetOutlinedIcon sx={{ fontSize: 20 }} />
        </IconButton>
        {allowAttachments && (
          <>
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || attachments.length >= MAX_ATTACHMENTS}
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: "10px",
                background: ACCENT_LIGHT,
                color: ACCENT,
                "&:hover": { background: "#F8D2DD" },
              }}
            >
              <AttachFileOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </>
        )}

        <TextField
          inputRef={textRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={disabled ? "No phone number on file" : placeholder}
          multiline
          maxRows={4}
          fullWidth
          disabled={disabled}
          sx={{
            // lets the field shrink instead of pushing the send button off
            // screen on narrow viewports
            minWidth: 0,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "14px",
              "& fieldset": { borderColor: PINK_BORDER },
              "&:hover fieldset": { borderColor: ACCENT },
              "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: "1.5px" },
            },
          }}
        />

        <Button
          sx={{
            ...greenGradientBtn,
            flexShrink: 0,
            ...(compact
              ? { px: 0, minWidth: 42, width: 42 }
              : { px: 2.5 }),
          }}
          onClick={handleSend}
          aria-label={sendLabel}
          title={sendLabel}
          disabled={sending || disabled || (!text.trim() && attachments.length === 0)}
        >
          {compact ? (
            sending ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <SendRoundedIcon sx={{ fontSize: 19 }} />
            )
          ) : sending ? (
            "Sending…"
          ) : (
            sendLabel
          )}
        </Button>
      </Box>

      <Popover
        open={!!emojiAnchor}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, width: 264, display: "flex", flexWrap: "wrap", gap: "2px" }}>
          {EMOJIS.map((emoji) => (
            <Typography
              key={emoji}
              onClick={() => insertEmoji(emoji)}
              sx={{
                fontSize: "20px",
                width: 30,
                height: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                borderRadius: "6px",
                "&:hover": { background: ACCENT_LIGHT },
              }}
            >
              {emoji}
            </Typography>
          ))}
        </Box>
      </Popover>

      <TemplatePicker
        anchorEl={templateAnchor}
        onClose={() => setTemplateAnchor(null)}
        onPick={insertTemplate}
        context={templateContext}
      />
    </Box>
  );
};

export default ChatComposer;
